import { apiService, type WorkspaceCatalogResponse } from '@/services/apiService'
import { connectivityService } from '@/services/connectivityService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { useOfflineStore } from '@/stores/offlineStore'
import type { TourListItem } from '@/types/tour'

function shouldUseOfflineOnly() {
  return !connectivityService.isEffectivelyOnline.value
}

export async function loadWorkspaceCatalog(): Promise<WorkspaceCatalogResponse> {
  const offlineStore = useOfflineStore()

  if (shouldUseOfflineOnly()) {
    return {
      workspaces: await offlineStore.listOfflineWorkspaceCatalog(),
      personalTourCount: 0,
    }
  }

  try {
    return await apiService.getWorkspacesForSearch()
  } catch (error) {
    logService.warn(LOG_CATEGORIES.OFFLINE, 'Workspace catalog falling back to offline packages', {
      error,
    })
    return {
      workspaces: await offlineStore.listOfflineWorkspaceCatalog(),
      personalTourCount: 0,
    }
  }
}

export async function loadLibraryToursFiltered(filters: {
  name?: string
  noWorkspace?: boolean
  dateFrom?: string
  dateTo?: string
}): Promise<TourListItem[]> {
  const offlineStore = useOfflineStore()

  if (!shouldUseOfflineOnly()) {
    try {
      const response = await apiService.getTours(filters)
      return response.results ?? []
    } catch (error) {
      logService.warn(
        LOG_CATEGORIES.OFFLINE,
        'Filtered library list falling back to offline packages',
        {
          error,
        },
      )
    }
  }

  const tours = await offlineStore.listOfflineLibraryTours()
  return tours.filter((tour) => {
    if (filters.name) {
      const q = filters.name.trim().toLowerCase()
      const haystack = `${tour.name} ${tour.description ?? ''}`.toLowerCase()
      if (!haystack.includes(q)) return false
    }

    if (filters.noWorkspace) {
      return tour.workspaces.length === 0
    }

    if (filters.dateFrom || filters.dateTo) {
      const start = tour.startDate ? Date.parse(tour.startDate) : null
      if (filters.dateFrom && start !== null && start < Date.parse(filters.dateFrom)) return false
      if (filters.dateTo && start !== null && start > Date.parse(filters.dateTo)) return false
    }

    return true
  })
}
